<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowPathIcon, ChevronLeftIcon, InboxIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import TicketAdminDetail from '@/components/features/tickets/admin/TicketAdminDetail.vue'
import { useTicketAdminStore, type AdminTicket } from '@/stores/useTicketAdminStore'

const route = useRoute()
const router = useRouter()
const store = useTicketAdminStore()

const tag = ref<'DEV' | 'TECH_PANDORA'>('DEV')
const search = ref('')
const selectedStatuses = ref<string[]>(['NEW', 'OPEN', 'WAITING', 'PAUSED'])

const arrowPathIcon = markRaw(ArrowPathIcon)
const chevronLeftIcon = markRaw(ChevronLeftIcon)

const STATUSES = [
  { value: 'NEW', label: 'NOUVEAU', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'OPEN', label: 'OUVERT', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'WAITING', label: 'EN ATTENTE', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'PAUSED', label: 'EN PAUSE', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  { value: 'RESOLVED', label: 'RÉSOLU', color: 'bg-teal-100 text-teal-800 border-teal-200' },
  { value: 'CLOSED', label: 'FERMÉ', color: 'bg-red-100 text-red-800 border-red-200' },
]

const statusByValue = computed(() => Object.fromEntries(STATUSES.map((s) => [s.value, s])))

async function refresh() {
  await store.fetchTickets({
    tag: tag.value,
    ticketStatus: selectedStatuses.value,
    search: search.value || undefined,
  })
}

let refreshTimeout: ReturnType<typeof setTimeout> | undefined
function debouncedRefresh() {
  if (refreshTimeout) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(refresh, 400)
}

function requesterShort(t: AdminTicket): string {
  const u = t.ticketRequester?.user
  if (u) return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email
  const c = t.ticketRequester?.contact
  if (c) return `${c.firstname ?? ''} ${c.lastname ?? ''}`.trim() || c.email
  return '—'
}

function assigneeShort(t: AdminTicket): string {
  const u = t.assignedUser
  if (!u) return '—'
  return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email
}

function openTicket(uuid: string) {
  router.push(`/admin/tickets/${uuid}`)
}

function goBackToList() {
  store.$patch({ ticket: null })
  router.push('/admin/tickets')
}

watch(
  () => route.params.uuid,
  async (uuid) => {
    if (uuid && typeof uuid === 'string') {
      await store.fetchTicket(uuid)
    } else {
      store.$patch({ ticket: null })
      await refresh()
    }
  },
  { immediate: true },
)

watch(search, debouncedRefresh)
watch([tag, selectedStatuses], debouncedRefresh, { deep: true })

onMounted(refresh)
</script>

<template>
  <div class="w-full flex flex-col gap-4 p-6">
    <!-- Header -->
    <template v-if="!store.ticket">
      <div class="flex items-center justify-between gap-4 flex-wrap">
        <div class="flex items-center gap-3">
          <Bs2Title title="Tickets — admin" />
          <span v-if="!store.loading" class="text-sm text-gray-500 font-medium mt-1">
            {{ store.total }} ticket(s)
          </span>
        </div>
        <ButtonDaisy text="Recharger" variant="secondary" :icon="arrowPathIcon" :disabled="store.loading" @click="refresh" />
      </div>
    </template>

    <template v-else>
      <ButtonDaisy text="Retour" variant="primary" :icon="chevronLeftIcon" @click="goBackToList" />
    </template>

    <!-- Détail -->
    <template v-if="store.ticket">
      <TicketAdminDetail :ticket="store.ticket" />
    </template>

    <!-- Liste + filtres -->
    <template v-else>
      <div class="card bg-base-100 rounded-lg p-4 shadow">
        <div class="flex flex-wrap gap-4 mb-4">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Tag</span>
            <select v-model="tag" class="select select-bordered select-sm">
              <option value="DEV">DEV</option>
              <option value="TECH_PANDORA">TECH_PANDORA</option>
            </select>
          </label>

          <label class="form-control flex-1 min-w-[200px]">
            <span class="label-text text-xs mb-1">Rechercher</span>
            <input
              v-model="search"
              type="text"
              placeholder="Titre, description, référence…"
              class="input input-bordered input-sm w-full"
            />
          </label>

          <div class="form-control">
            <span class="label-text text-xs mb-1">Statuts</span>
            <div class="flex gap-1 flex-wrap">
              <label
                v-for="s in STATUSES"
                :key="s.value"
                class="cursor-pointer flex items-center gap-1 border rounded px-2 py-1 text-xs"
                :class="selectedStatuses.includes(s.value) ? s.color : 'opacity-50'"
              >
                <input
                  type="checkbox"
                  :value="s.value"
                  v-model="selectedStatuses"
                  class="checkbox checkbox-xs"
                />
                {{ s.label }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="store.loading" class="flex justify-center py-12">
          <ArrowPathIcon class="w-8 h-8 animate-spin text-base-content/40" />
        </div>

        <div v-else-if="store.tickets.length === 0" class="flex flex-col items-center py-12 gap-2 text-base-content/60">
          <InboxIcon class="w-12 h-12" />
          <p>Aucun ticket</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Titre</th>
                <th>Demandeur</th>
                <th>Assigné</th>
                <th>Tags</th>
                <th>Statut</th>
                <th>MAJ</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="t in store.tickets"
                :key="t.uuid"
                class="cursor-pointer hover:bg-base-200"
                @click="openTicket(t.uuid)"
              >
                <td class="font-mono text-xs">#{{ t.internalReference }}</td>
                <td class="max-w-md truncate">{{ t.title }}</td>
                <td>{{ requesterShort(t) }}</td>
                <td>{{ assigneeShort(t) }}</td>
                <td>
                  <span v-for="tag in t.tags" :key="tag" class="badge badge-outline badge-xs mr-1">{{ tag }}</span>
                </td>
                <td>
                  <span
                    class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
                    :class="statusByValue[t.ticketStatus]?.color ?? 'bg-base-200'"
                  >
                    {{ statusByValue[t.ticketStatus]?.label ?? t.ticketStatus }}
                  </span>
                </td>
                <td class="text-xs text-base-content/60">{{ new Date(t.dateUpdated).toLocaleDateString('fr-FR') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
